// ============================================================================
// Shared presentational kit for the OINP assessment screens.
// Follows the same conventions as CRSCalculator/CRSResults: styled-components,
// $darkMode / $scheme transient props, getColorScheme for brand colour.
// ============================================================================

import React from 'react';
import styled, { keyframes } from 'styled-components';
import { getColorScheme } from '../../theme/colorSchemes';

export const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const Wrapper = styled.div`
  padding: 16px;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  animation: ${fadeIn} 0.3s ease-out;
  height: 100%;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;

  *, *::before, *::after { box-sizing: border-box; }

  &::-webkit-scrollbar { display: none; }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

// ---------------------------------------------------------------- progress --
const ProgressOuter = styled.div`
  margin-bottom: 16px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 5px;
  font-size: 11px;
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.5)' : '#6b7a8d')};
`;

const ProgressTrack = styled.div`
  height: 5px;
  border-radius: 3px;
  background: ${p => (p.$darkMode ? '#333' : '#f1f5f9')};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 3px;
  width: ${p => p.$pct}%;
  transition: width 0.4s ease;
  background: ${p => {
    const c = getColorScheme(p.$scheme);
    return `linear-gradient(90deg, ${c.primary}, ${c.primaryLight || c.primary})`;
  }};
`;

const StartOverLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  text-decoration: underline;
  cursor: pointer;
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.5)' : '#8a7250')};
`;

export const Progress = ({ label, pct, darkMode, scheme, onStartOver, startOverLabel }) => (
  <ProgressOuter>
    <ProgressLabel $darkMode={darkMode}>
      <span>{label}</span>
      {onStartOver && (
        <StartOverLink type="button" $darkMode={darkMode} onClick={onStartOver}>
          {startOverLabel}
        </StartOverLink>
      )}
    </ProgressLabel>
    <ProgressTrack $darkMode={darkMode}>
      <ProgressFill $pct={pct} $scheme={scheme} />
    </ProgressTrack>
  </ProgressOuter>
);

// ------------------------------------------------------------------ header --
export const Eyebrow = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  margin-bottom: 6px;
  color: ${p => {
    const c = getColorScheme(p.$scheme);
    return p.$darkMode ? c.primaryLight || c.primary : c.primary;
  }};
`;

export const StepTitle = styled.h3`
  margin: 0 0 6px 0;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.95)' : '#1a2332')};
`;

export const StepSubtitle = styled.p`
  margin: 0 0 16px 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.55)' : '#6b7a8d')};
`;

export const FieldLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  margin: 16px 0 8px;
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.5)' : '#8a7250')};
`;

// ------------------------------------------------------------------ options --
export const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: ${p => (p.$cols === 2 ? '1fr 1fr' : '1fr')};
  gap: 8px;
  width: 100%;
  min-width: 0;

  @media (max-width: 380px) {
    grid-template-columns: 1fr;
  }
`;

const CardButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  text-align: left;
  cursor: pointer;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 0;

  border: 1.5px solid ${p => {
    const c = getColorScheme(p.$scheme);
    if (p.$selected) return c.primary;
    return p.$darkMode ? 'rgba(255,255,255,0.1)' : '#e8ecf1';
  }};
  background: ${p => {
    const c = getColorScheme(p.$scheme);
    if (p.$selected) return p.$darkMode ? `${c.primary}33` : `${c.primary}1f`;
    return p.$darkMode ? 'rgba(255,255,255,0.04)' : '#ffffff';
  }};

  &:hover {
    transform: translateY(-1px);
    border-color: ${p => getColorScheme(p.$scheme).primary};
  }
  &:active { transform: translateY(0); }
`;

const CardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.div`
  font-size: 13.5px;
  font-weight: 650;
  line-height: 1.35;
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.95)' : '#1a2332')};
`;

const CardDetail = styled.div`
  margin-top: 3px;
  font-size: 11.5px;
  line-height: 1.45;
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.5)' : '#6b7a8d')};
`;

const CardBadge = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  color: ${p => {
    const c = getColorScheme(p.$scheme);
    return p.$darkMode ? c.primaryLight || c.primary : c.primary;
  }};
`;

export const OptionCard = ({ title, detail, badge, selected, onClick, darkMode, scheme }) => (
  <CardButton
    type="button"
    $selected={selected}
    $darkMode={darkMode}
    $scheme={scheme}
    onClick={onClick}
    aria-pressed={selected}
  >
    <CardBody>
      <CardTitle $darkMode={darkMode}>{title}</CardTitle>
      {detail && <CardDetail $darkMode={darkMode}>{detail}</CardDetail>}
    </CardBody>
    {badge && <CardBadge $darkMode={darkMode} $scheme={scheme}>{badge}</CardBadge>}
  </CardButton>
);

// -------------------------------------------------------------------- input --
export const TextInput = styled.input`
  width: 100%;
  padding: 11px 13px;
  border-radius: 10px;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 13px;
  outline: none;
  transition: border-color 0.2s;
  border: 1.5px solid ${p => (p.$darkMode ? 'rgba(255,255,255,0.14)' : '#e2e8f0')};
  background: ${p => (p.$darkMode ? 'rgba(255,255,255,0.05)' : '#ffffff')};
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.92)' : '#1a2332')};

  &::placeholder { color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8')}; }
  &:focus { border-color: ${p => getColorScheme(p.$scheme).primary}; }
`;

export const InlineRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
  width: 100%;
  min-width: 0;
`;

export const ErrorText = styled.div`
  margin-top: 6px;
  font-size: 11.5px;
  color: #dc3545;
`;

export const HelpLink = styled.a`
  display: inline-block;
  margin-top: 8px;
  font-size: 11.5px;
  text-decoration: underline;
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.55)' : '#6b7a8d')};
`;

// -------------------------------------------------------------------- cards --
export const Card = styled.div`
  padding: 14px;
  border-radius: 12px;
  margin-bottom: 12px;
  background: ${p => (p.$darkMode ? '#252525' : '#f8fafc')};
  border: 1px solid ${p => (p.$darkMode ? 'rgba(255,255,255,0.07)' : '#eef2f6')};
`;

export const CardHeading = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 10px;
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.45)' : '#8a7250')};
`;

export const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 7px 0;
  font-size: 12.5px;
  border-bottom: 1px solid ${p => (p.$darkMode ? 'rgba(255,255,255,0.06)' : '#e9eef3')};
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.75)' : '#3f4a58')};

  &:last-child { border-bottom: none; }
`;

export const RowValue = styled.span`
  flex-shrink: 0;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${p => p.$muted
    ? (p.$darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8')
    : (p.$darkMode ? 'rgba(255,255,255,0.95)' : '#1a2332')};
`;

export const Note = styled.div`
  padding: 11px 13px;
  border-radius: 10px;
  font-size: 11.5px;
  line-height: 1.55;
  margin-bottom: 12px;
  background: ${p => {
    if (p.$tone === 'warn') return p.$darkMode ? 'rgba(217,119,6,0.14)' : '#fffbeb';
    if (p.$tone === 'error') return p.$darkMode ? 'rgba(220,53,69,0.14)' : '#fef2f2';
    return p.$darkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9';
  }};
  border: 1px solid ${p => {
    if (p.$tone === 'warn') return p.$darkMode ? 'rgba(217,119,6,0.3)' : '#fde68a';
    if (p.$tone === 'error') return p.$darkMode ? 'rgba(220,53,69,0.3)' : '#fecaca';
    return p.$darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0';
  }};
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.75)' : '#4b5563')};
`;

export const Disclaimer = styled.p`
  margin: 16px 0 0;
  font-size: 10px;
  line-height: 1.5;
  text-align: center;
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8')};
`;

// ---------------------------------------------------------------- gate list --
const GateRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 6px 0;
  font-size: 12.5px;
  font-weight: 600;
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.85)' : '#1a2332')};
`;

const GateNote = styled.div`
  margin: 2px 0 6px 27px;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.5;
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.5)' : '#6b7a8d')};
`;

const STATUS_COLOR = { pass: '#16a34a', warn: '#d97706', fail: '#dc3545' };

const GateIcon = ({ status }) => {
  const color = STATUS_COLOR[status] || '#94a3b8';
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" />
      {status === 'pass' && <polyline points="7.5 12.5 10.5 15.5 16.5 9" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />}
      {status === 'warn' && <><line x1="12" y1="7.5" x2="12" y2="13" stroke={color} strokeWidth="2.2" strokeLinecap="round" /><circle cx="12" cy="16.5" r="1.2" fill={color} /></>}
      {status === 'fail' && <><line x1="8.5" y1="8.5" x2="15.5" y2="15.5" stroke={color} strokeWidth="2.2" strokeLinecap="round" /><line x1="15.5" y1="8.5" x2="8.5" y2="15.5" stroke={color} strokeWidth="2.2" strokeLinecap="round" /></>}
    </svg>
  );
};

/**
 * Rows may supply their own `labelKey`; otherwise the gate id is mapped through
 * GATE_LABEL_KEYS. Callers with parameterised copy should pass labelKey so we
 * never render an uninterpolated template.
 */
export const GateList = ({ gates, darkMode, tr }) => (
  <div>
    {gates.map(gate => (
      <React.Fragment key={gate.id}>
        <GateRow $darkMode={darkMode}>
          <GateIcon status={gate.status} />
          <span>{tr(gate.labelKey || GATE_LABEL_KEYS[gate.id] || gate.id)}</span>
        </GateRow>
        {gate.note && <GateNote $darkMode={darkMode}>{tr(gate.note)}</GateNote>}
      </React.Fragment>
    ))}
  </div>
);

const GATE_LABEL_KEYS = {
  'work-experience': 'gateExperience',
  'language-minimum': 'gateLanguage',
  'post-secondary-credential': 'gateCredential',
  'eca': 'gateEca',
  'legal-status': 'gateStatus',
  'ohip-billing': 'gateOhip',
  'cpso-certificate': 'gateCpso',
  'ontario-practice': 'gateOntarioPractice',
};

// ------------------------------------------------------------------- gauge ---
export const Gauge = ({ value, max, color, darkMode, caption }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const circumference = 534;
  return (
    <div style={{ textAlign: 'center', marginBottom: 14 }}>
      <div style={{ position: 'relative', width: 156, height: 156, margin: '0 auto' }}>
        <svg viewBox="0 0 200 200" style={{ width: 156, height: 156 }}>
          <circle cx="100" cy="100" r="85" fill="none" stroke={darkMode ? '#333' : '#f1f5f9'} strokeWidth="12" />
          <circle
            cx="100" cy="100" r="85" fill="none" stroke={color} strokeWidth="12"
            strokeDasharray={`${(pct / 100) * circumference} ${circumference}`}
            strokeLinecap="round" transform="rotate(-90 100 100)"
            style={{ transition: 'stroke-dasharray 1s ease' }}
          />
        </svg>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}>
          <div style={{ fontSize: 40, fontWeight: 800, lineHeight: 1, color: darkMode ? 'rgba(255,255,255,0.95)' : '#1a2332' }}>
            {value}
          </div>
          <div style={{ fontSize: 11, marginTop: 3, color: darkMode ? 'rgba(255,255,255,0.4)' : '#6b7a8d' }}>
            {caption}
          </div>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ buttons --
export const NavRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  width: 100%;
  min-width: 0;
`;

export const BackBtn = styled.button`
  padding: 10px 18px;
  border-radius: 10px;
  border: 1.5px solid ${p => (p.$darkMode ? '#444' : '#e2e8f0')};
  background: transparent;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.7)' : '#64748b')};
  transition: background 0.2s;

  &:hover { background: ${p => (p.$darkMode ? '#333' : '#f1f5f9')}; }
`;

export const NextBtn = styled.button`
  padding: 10px 22px;
  border-radius: 10px;
  border: none;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s;
  background: ${p => {
    if (p.disabled) return p.$darkMode ? '#333' : '#e2e8f0';
    const c = getColorScheme(p.$scheme);
    return `linear-gradient(135deg, ${c.primary}, ${c.primaryDark})`;
  }};
  color: ${p => (p.disabled ? (p.$darkMode ? '#666' : '#94a3b8') : '#fff')};
  /* NB: the scheme exposes accentGlow/accentShadow — there is no shadowColor
     key, despite CRSCalculator referencing one. */
  box-shadow: ${p => (p.disabled ? 'none' : `0 4px 12px ${getColorScheme(p.$scheme).accentGlow}`)};

  &:hover:not(:disabled) { transform: translateY(-1px); }
`;

export const PrimaryBtn = styled(NextBtn)`
  width: 100%;
  padding: 12px 20px;
  font-size: 13.5px;
`;

export const GhostBtn = styled(BackBtn)`
  width: 100%;
  padding: 11px 20px;
  margin-top: 8px;
`;
